import { format, isValid, parseISO } from 'date-fns';
import { useMap } from 'es-map-widget';
import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MAP_LAYER_NAMES } from '../../../constants';
import { useApi } from '../../../hooks/useApi';
import { DEFAULT_DROUGHT_FILTER, DROUGHT_FILTER_ITEMS } from './droughtWarning.constants';

const DroughtWarningContext = createContext(null);

const getTodayDate = () => format(new Date(), 'yyyy-MM-dd');

const getNormalizedDate = (date) => {
    if (!date) {
        return getTodayDate();
    }

    const parsedDate = parseISO(date);

    return isValid(parsedDate) ? format(parsedDate, 'yyyy-MM-dd') : getTodayDate();
};

const getNormalizedFilter = (filter) => {
    if (DROUGHT_FILTER_ITEMS.some((item) => item.value === filter)) {
        return filter;
    }

    return DEFAULT_DROUGHT_FILTER;
};

const getNormalizedOpacity = (opacity) => {
    const parsedOpacity = Number(opacity);

    if (Number.isNaN(parsedOpacity)) {
        return 1;
    }

    return Math.min(1, Math.max(0.1, parsedOpacity / 10));
};

export function DroughtWarningProvider({ children }) {
    const map = useMap();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedDate = getNormalizedDate(searchParams.get('date'));
    const selectedFilter = getNormalizedFilter(searchParams.get('filter'));
    const selectedOpacity = getNormalizedOpacity(searchParams.get('opacity'));
    const selectedMeparCode = searchParams.get('meparCode') || '';
    const selectedGridId = searchParams.get('gridId') || '';
    const hasDroughtInfoSelection = Boolean(selectedMeparCode || selectedGridId);
    const [droughtGrids, droughtGridsLoading] = useApi(
        (api, params) =>
            api.drought.getDroughtGrids(
                {
                    date: selectedDate,
                },
                params
            ),
        [selectedDate]
    );
    const [droughtInfo, droughtInfoLoading] = useApi(
        (api, params) =>
            api.drought.getDroughtInfo(
                {
                    date: selectedDate,
                    ...(selectedGridId
                        ? { gridId: Number(selectedGridId) }
                        : { meparCode: selectedMeparCode }),
                },
                params
            ),
        [selectedDate, selectedMeparCode, selectedGridId],
        (data) => data || { items: [], mepar_geom: null },
        !hasDroughtInfoSelection
    );
    const droughtInfoRows = useMemo(() => {
        if (!hasDroughtInfoSelection || droughtInfoLoading) {
            return [];
        }

        return droughtInfo?.items || [];
    }, [droughtInfo, droughtInfoLoading, hasDroughtInfoSelection]);
    const selectedMeparGeom =
        !selectedMeparCode || droughtInfoLoading ? null : droughtInfo?.mepar_geom || null;

    useEffect(() => {
        if (
            searchParams.get('date') === selectedDate &&
            searchParams.get('filter') === selectedFilter &&
            getNormalizedOpacity(searchParams.get('opacity')) === selectedOpacity
        ) {
            return;
        }

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('date', selectedDate);
        nextSearchParams.set('filter', selectedFilter);
        nextSearchParams.set('opacity', String(Math.round(selectedOpacity * 10)));
        setSearchParams(nextSearchParams, { replace: true });
    }, [searchParams, selectedDate, selectedFilter, selectedOpacity, setSearchParams]);

    useEffect(() => {
        if (droughtGridsLoading || !droughtGrids?.length) {
            map.setLayerData(MAP_LAYER_NAMES.DROUGHT, { geoms: [] });
            map.setLayerVisibility(MAP_LAYER_NAMES.DROUGHT, false);
            return;
        }

        map.setLayerData(MAP_LAYER_NAMES.DROUGHT, {
            geoms: droughtGrids,
            filter: selectedFilter,
            opacity: selectedOpacity,
            selectedGridId,
        });
        map.setLayerVisibility(MAP_LAYER_NAMES.DROUGHT, true);
    }, [map, droughtGrids, droughtGridsLoading, selectedFilter, selectedGridId, selectedOpacity]);

    useEffect(() => {
        if (!selectedMeparCode || !selectedMeparGeom) {
            map.setLayerData(MAP_LAYER_NAMES.MEPAR, { geoms: [] });
            map.setLayerVisibility(MAP_LAYER_NAMES.MEPAR, false);
            return;
        }

        const meparLayerData = {
            geom: selectedMeparGeom,
            layerName: selectedMeparCode,
        };

        map.setLayerData(MAP_LAYER_NAMES.MEPAR, {
            geoms: [meparLayerData],
        });
        map.setLayerVisibility(MAP_LAYER_NAMES.MEPAR, true);
        map.zoomToGeomsExtent([meparLayerData]);
    }, [map, selectedMeparCode, selectedMeparGeom]);

    useEffect(() => {
        return () => {
            map.setLayerData(MAP_LAYER_NAMES.DROUGHT, { geoms: [] });
            map.setLayerVisibility(MAP_LAYER_NAMES.DROUGHT, false);
            map.setLayerData(MAP_LAYER_NAMES.MEPAR, { geoms: [] });
            map.setLayerVisibility(MAP_LAYER_NAMES.MEPAR, false);
        };
    }, [map]);

    const setSelectedDate = useCallback(
        (nextDate) => {
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.set('date', nextDate);
            setSearchParams(nextSearchParams);
        },
        [searchParams, setSearchParams]
    );

    const setSelectedFilter = useCallback(
        (nextFilter) => {
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.set('filter', nextFilter);
            setSearchParams(nextSearchParams);
        },
        [searchParams, setSearchParams]
    );

    const setSelectedOpacity = useCallback(
        (nextOpacity) => {
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.set('opacity', String(nextOpacity));
            setSearchParams(nextSearchParams);
        },
        [searchParams, setSearchParams]
    );

    const setSelectedMeparCode = useCallback(
        (nextMeparCode) => {
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.delete('gridId');

            if (!nextMeparCode) {
                nextSearchParams.delete('meparCode');
                setSearchParams(nextSearchParams);
                return;
            }

            nextSearchParams.set('meparCode', nextMeparCode);
            setSearchParams(nextSearchParams);
        },
        [searchParams, setSearchParams]
    );

    const setSelectedGridId = useCallback(
        (nextGridId) => {
            const nextSearchParams = new URLSearchParams(searchParams);

            if (!nextGridId) {
                nextSearchParams.delete('gridId');
                setSearchParams(nextSearchParams);
                return;
            }

            nextSearchParams.delete('meparCode');
            nextSearchParams.set('gridId', String(nextGridId));
            setSearchParams(nextSearchParams);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        const internalMap = map?.map;

        if (!internalMap) {
            return undefined;
        }

        const handleDroughtGridClick = (event) => {
            const droughtLayer = internalMap
                .getAllLayers()
                .find((layer) => layer.get('name') === MAP_LAYER_NAMES.DROUGHT);

            if (!droughtLayer || !droughtLayer.getVisible()) {
                return;
            }

            const feature = internalMap.forEachFeatureAtPixel(
                event.pixel,
                (clickedFeature) => clickedFeature,
                {
                    hitTolerance: 2,
                    layerFilter: (layer) => layer === droughtLayer,
                }
            );

            if (!feature) {
                if (selectedGridId) {
                    setSelectedGridId(null);
                }
                return;
            }

            const gridId = feature.get('grid_id');

            if (typeof gridId === 'undefined' || gridId === null) {
                return;
            }

            setSelectedGridId(gridId);
        };

        internalMap.on('singleclick', handleDroughtGridClick);

        return () => {
            internalMap.un('singleclick', handleDroughtGridClick);
        };
    }, [map, selectedGridId, setSelectedGridId]);

    const value = useMemo(
        () => ({
            selectedDate,
            selectedFilter,
            selectedOpacity,
            selectedMeparCode,
            selectedGridId,
            droughtGrids: droughtGridsLoading ? [] : droughtGrids || [],
            droughtGridsLoading,
            droughtInfoRows: droughtInfoLoading ? [] : droughtInfoRows || [],
            droughtInfoLoading,
            setSelectedDate,
            setSelectedFilter,
            setSelectedOpacity,
            setSelectedMeparCode,
            setSelectedGridId,
        }),
        [
            selectedDate,
            selectedFilter,
            selectedOpacity,
            selectedMeparCode,
            selectedGridId,
            droughtGrids,
            droughtGridsLoading,
            droughtInfoLoading,
            droughtInfoRows,
            setSelectedDate,
            setSelectedFilter,
            setSelectedOpacity,
            setSelectedMeparCode,
            setSelectedGridId,
        ]
    );

    return (
        <DroughtWarningContext.Provider value={value}>{children}</DroughtWarningContext.Provider>
    );
}

export function useDroughtWarning() {
    const context = useContext(DroughtWarningContext);

    if (!context) {
        throw new Error('useDroughtWarning must be used within a DroughtWarningProvider');
    }

    return context;
}
